/**
 * ComponentRegistry - Maps type strings to React components
 * 
 * This registry allows the template engine to dynamically render
 * special components based on their type string in the template.
 * 
 * The registry is extensible - new components can be registered
 * at runtime by AI agents or through configuration.
 */

import React from 'react';
import dynamic from 'next/dynamic';

// ============================================================================
// Types
// ============================================================================

/**
 * A registered component can be:
 * - A React component
 * - A lazy-loaded component (dynamic import)
 * - A component factory function
 */
export type RegisteredComponent = 
  | React.ComponentType<any>
  | React.LazyExoticComponent<React.ComponentType<any>>;

/**
 * Component registration entry
 */
export interface ComponentEntry {
  /** The component or lazy component */
  component: RegisteredComponent;
  /** Whether the component should be client-side only */
  clientOnly?: boolean;
  /** Default props to merge with provided props */
  defaultProps?: Record<string, unknown>;
  /** Description for documentation */
  description?: string;
}

/**
 * Registry storage type
 */
type RegistryMap = Map<string, ComponentEntry>;

// ============================================================================
// Dynamic Imports for Special Components
// ============================================================================

// Lazy load special components to avoid circular dependencies
// and improve code splitting

const S3FilePreview = dynamic(
  () => import('@/components/shared/S3FilePreview'),
  { ssr: false }
);

const MarkdownRenderer = dynamic(
  () => import('@/components/ui/MarkdownRenderer').then(mod => ({ default: mod.MarkdownRenderer })),
  { ssr: true }
);

const ColorCircle = dynamic(
  () => import('@/components/ui/ColorCircle'),
  { ssr: true }
);

const ImageCircle = dynamic(
  () => import('@/components/ui/ImageCircle').then(mod => ({ default: mod.default })),
  { ssr: true }
);

const SmoothReveal = dynamic(
  () => import('@/components/shared/SmoothReveal').then(mod => ({ default: mod.SmoothReveal })),
  { ssr: false }
);

const Button = dynamic(
  () => import('@/components/ui/button').then(mod => ({ default: mod.Button })),
  { ssr: true }
);

const Link = dynamic(
  () => import('next/link').then(mod => ({ default: mod.default })),
  { ssr: true }
);

// Simple Icon component for lucide-react icons
const HRServiceImage = dynamic(
  () => import('@/components/shared/HRServiceImage'),
  { ssr: false }
);

const Accordion = dynamic(
  () => import('@/components/ui/accordion').then(mod => ({ default: mod.Accordion })),
  { ssr: true }
);

const AccordionItem = dynamic(
  () => import('@/components/ui/accordion').then(mod => ({ default: mod.AccordionItem })),
  { ssr: true }
);

const AccordionTrigger = dynamic(
  () => import('@/components/ui/accordion').then(mod => ({ default: mod.AccordionTrigger })),
  { ssr: true }
);

const AccordionContent = dynamic(
  () => import('@/components/ui/accordion').then(mod => ({ default: mod.AccordionContent })),
  { ssr: true }
);

const BackgroundVideo = dynamic(
  () => import('@/components/shared/BackgroundVideo').then(mod => ({ default: mod.BackgroundVideo })),
  { ssr: false }
);

const HeroCarousel = dynamic(
  () => import('@/components/shared/HeroCarousel'),
  { ssr: false }
);

const LucideIcon = dynamic(
  () => import('lucide-react').then(mod => {
    // Create a wrapper component that can render any lucide icon
    const IconWrapper = ({ name, className, style, ...props }: { name: string | number; className?: string; style?: React.CSSProperties; [key: string]: unknown }) => {
      // Map index to icon name for BeyondStandards section
      const iconNames = ['Headphones', 'Wifi', 'Bot'];
      let iconName: string;
      
      if (typeof name === 'number') {
        iconName = iconNames[name] || iconNames[0];
      } else {
        iconName = name;
      }
      
      // Strip quotes from icon name if present (handles template strings like "Sparkles")
      if (typeof iconName === 'string') {
        iconName = iconName.trim().replace(/^["']|["']$/g, '');
      }
      
      const IconComponent = (mod as any)[iconName];
      if (!IconComponent) {
        console.warn(`Icon "${iconName}" not found in lucide-react`);
        return null;
      }
      return React.createElement(IconComponent, { className, style, ...props });
    };
    
    return { default: IconWrapper };
  }),
  { ssr: false }
);

// ============================================================================
// Registry Implementation
// ============================================================================

/**
 * The component registry singleton
 */
class ComponentRegistryClass {
  private registry: RegistryMap = new Map();
  private initialized = false;

  constructor() {
    this.initializeDefaults();
  }

  /**
   * Initialize with default components
   */
  private initializeDefaults(): void {
    if (this.initialized) return;
    
    // Register special components
    this.register('S3FilePreview', {
      component: S3FilePreview,
      clientOnly: true,
      description: 'Renders images and videos from S3 storage',
      defaultProps: {
        disablePreviewModal: false,
        disableSkeleton: false,
      },
    });

    this.register('BackgroundVideo', {
      component: BackgroundVideo,
      clientOnly: true,
      description: 'Renders background video with fallback support',
      defaultProps: {
        disableVideo: false,
        disableFallback: false,
      },
    });

    this.register('Markdown', {
      component: MarkdownRenderer,
      description: 'Renders markdown content as HTML',
    });

    this.register('MarkdownRenderer', {
      component: MarkdownRenderer,
      description: 'Renders markdown content as HTML (alias)',
    });

    this.register('ColorCircle', {
      component: ColorCircle,
      description: 'Renders a colored circle/shape element',
    });

    this.register('ImageCircle', {
      component: ImageCircle,
      description: 'Renders a circular image element',
    });

    this.register('LucideIcon', {
      component: LucideIcon,
      clientOnly: true,
      description: 'Renders lucide-react icons dynamically',
    });

    this.register('SmoothReveal', {
      component: SmoothReveal,
      clientOnly: true,
      description: 'Animates content reveal with smooth transitions',
    });

    this.register('Button', {
      component: Button,
      description: 'Styled button component with variants and sizes',
      defaultProps: {
        variant: 'default',
        size: 'default',
      },
    });

    this.register('Link', {
      component: Link,
      description: 'Next.js Link component for client-side navigation',
    });

    this.register('HRServiceImage', {
      component: HRServiceImage,
      clientOnly: true,
      description: 'HR service card image with title-based fallback',
    });

    this.register('Accordion', {
      component: Accordion,
      clientOnly: true,
      description: 'Radix UI Accordion root component',
      defaultProps: {
        type: 'single',
        collapsible: true,
      },
    });

    this.register('AccordionItem', {
      component: AccordionItem,
      clientOnly: true,
      description: 'Radix UI Accordion item component',
    });

    this.register('AccordionTrigger', {
      component: AccordionTrigger,
      clientOnly: true,
      description: 'Radix UI Accordion trigger component',
    });

    this.register('AccordionContent', {
      component: AccordionContent,
      clientOnly: true,
      description: 'Radix UI Accordion content component',
    });

    this.register('HeroCarousel', {
      component: HeroCarousel,
      clientOnly: true,
      description: 'Full-screen hero carousel with rotating banners',
      defaultProps: {
        autoplay: true,
        autoplayInterval: 6000,
        showArrows: true,
        showDots: true,
        showProgress: false,
        pauseOnHover: true,
      },
    });

    // Note: HTML elements are handled separately in NodeRenderer
    // They don't need to be in the registry
    
    this.initialized = true;
  }

  /**
   * Register a new component
   */
  register(type: string, entry: ComponentEntry): void {
    this.registry.set(type, entry);
  }

  /**
   * Unregister a component
   */
  unregister(type: string): boolean {
    return this.registry.delete(type);
  }

  /**
   * Check if a type is registered as a special component
   */
  has(type: string): boolean {
    return this.registry.has(type);
  }

  /**
   * Get a registered component entry
   */
  get(type: string): ComponentEntry | undefined {
    return this.registry.get(type);
  }

  /**
   * Get the component for a type
   */
  getComponent(type: string): RegisteredComponent | undefined {
    return this.registry.get(type)?.component;
  }

  /**
   * Get all registered types
   */
  getRegisteredTypes(): string[] {
    return Array.from(this.registry.keys());
  }

  /**
   * Get registry info for documentation
   */
  getRegistryInfo(): Array<{ type: string; description?: string; clientOnly?: boolean }> {
    return Array.from(this.registry.entries()).map(([type, entry]) => ({
      type,
      description: entry.description,
      clientOnly: entry.clientOnly,
    }));
  }

  /**
   * Check if a type is an HTML element
   */
  isHTMLElement(type: string): boolean {
    return HTML_ELEMENTS.has(type);
  }

  /**
   * Check if a type is a special component (in registry)
   */
  isSpecialComponent(type: string): boolean {
    return this.registry.has(type);
  }

  /**
   * Get the appropriate renderer type for a given type string
   */
  getRendererType(type: string): 'html' | 'special' | 'unknown' {
    if (this.isHTMLElement(type)) return 'html';
    if (this.isSpecialComponent(type)) return 'special';
    return 'unknown';
  }
}

// ============================================================================
// HTML Elements Set
// ============================================================================

/**
 * Set of valid HTML element types
 */
export const HTML_ELEMENTS = new Set([
  // Document sections
  'html', 'head', 'body',
  
  // Content sectioning
  'address', 'article', 'aside', 'footer', 'header', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'main', 'nav', 'section',
  
  // Text content
  'blockquote', 'dd', 'div', 'dl', 'dt', 'figcaption', 'figure', 'hr', 'li', 'ol', 'p',
  'pre', 'ul',
  
  // Inline text semantics
  'a', 'abbr', 'b', 'bdi', 'bdo', 'br', 'cite', 'code', 'data', 'dfn', 'em', 'i', 'kbd',
  'mark', 'q', 'rp', 'rt', 'ruby', 's', 'samp', 'small', 'span', 'strong', 'sub', 'sup',
  'time', 'u', 'var', 'wbr',
  
  // Image and multimedia
  'area', 'audio', 'img', 'map', 'track', 'video',
  
  // Embedded content
  'embed', 'iframe', 'object', 'param', 'picture', 'portal', 'source',
  
  // SVG and MathML
  'svg', 'math',

  // SVG elements
  'animate', 'animateMotion', 'animateTransform', 'circle', 'clipPath', 'defs', 'desc',
  'ellipse', 'feBlend', 'feColorMatrix', 'feComponentTransfer', 'feComposite', 'feConvolveMatrix',
  'feDiffuseLighting', 'feDisplacementMap', 'feDistantLight', 'feDropShadow', 'feFlood',
  'feFuncA', 'feFuncB', 'feFuncG', 'feFuncR', 'feGaussianBlur', 'feImage', 'feMerge', 'feMergeNode',
  'feMorphology', 'feOffset', 'fePointLight', 'feSpecularLighting', 'feSpotLight', 'feTile',
  'feTurbulence', 'filter', 'foreignObject', 'g', 'image', 'line', 'linearGradient', 'marker',
  'mask', 'metadata', 'mpath', 'path', 'pattern', 'polygon', 'polyline', 'radialGradient',
  'rect', 'set', 'stop', 'symbol', 'text', 'textPath', 'title', 'tspan', 'use', 'view',
  
  // Scripting
  'canvas', 'noscript', 'script',
  
  // Demarcating edits
  'del', 'ins',
  
  // Table content
  'caption', 'col', 'colgroup', 'table', 'tbody', 'td', 'tfoot', 'th', 'thead', 'tr',
  
  // Forms
  'button', 'datalist', 'fieldset', 'form', 'input', 'label', 'legend', 'meter',
  'optgroup', 'option', 'output', 'progress', 'select', 'textarea',
  
  // Interactive elements
  'details', 'dialog', 'menu', 'summary',
  
  // Web Components
  'slot', 'template',
  
  // Style tag (for injecting CSS)
  'style',
]);

/**
 * Elements that are self-closing (void elements)
 */
export const VOID_ELEMENTS = new Set([
  'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta',
  'param', 'source', 'track', 'wbr',
]);

/**
 * Check if an element type is a void (self-closing) element
 */
export function isVoidElement(type: string): boolean {
  return VOID_ELEMENTS.has(type);
}

// ============================================================================
// Singleton Export
// ============================================================================

/**
 * Singleton instance of the component registry
 */
export const ComponentRegistry = new ComponentRegistryClass();

export default ComponentRegistry;

